import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';

const CaseStudyForm = ({ caseStudy, onClose, onSave }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    client: '',
    industry: '',
    service: '',
    challenge: '',
    solution: '',
    results: ['', '', ''],
    duration: '',
    image: '',
    is_published: true
  });

  useEffect(() => {
    if (caseStudy) {
      setFormData(caseStudy);
    }
  }, [caseStudy]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleResultChange = (index, value) => {
    const newResults = [...formData.results];
    newResults[index] = value;
    setFormData({ ...formData, results: newResults });
  };

  const addResult = () => {
    setFormData({ ...formData, results: [...formData.results, ''] });
  };

  const removeResult = (index) => {
    const newResults = formData.results.filter((_, i) => i !== index);
    setFormData({ ...formData, results: newResults });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('admin_token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const url = caseStudy
        ? `${BACKEND_URL}/api/admin/case-studies/${caseStudy.id}`
        : `${BACKEND_URL}/api/admin/case-studies`;
      
      const response = await fetch(url, {
        method: caseStudy ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          results: formData.results.filter(r => r.trim() !== '')
        })
      });

      if (!response.ok) throw new Error('Failed to save');

      toast({
        title: "Success",
        description: `Case study ${caseStudy ? 'updated' : 'created'} successfully`
      });
      
      onSave();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save case study",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-3xl w-full my-8">
        <div className="p-6 border-b border-slate-200 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-slate-900">
            {caseStudy ? 'Edit Case Study' : 'New Case Study'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Client Name *
              </label>
              <Input
                name="client"
                value={formData.client}
                onChange={handleChange}
                required
                placeholder="Fortune 500 Company"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Industry *
              </label>
              <Input
                name="industry"
                value={formData.industry}
                onChange={handleChange}
                required
                placeholder="Manufacturing"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Service *
              </label>
              <Input
                name="service"
                value={formData.service}
                onChange={handleChange}
                required
                placeholder="Oracle ERP Implementation"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Duration *
              </label>
              <Input
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                required
                placeholder="8 months"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Challenge *
            </label>
            <Textarea
              name="challenge"
              value={formData.challenge}
              onChange={handleChange}
              required
              rows={3}
              placeholder="Describe the client's challenge..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Solution *
            </label>
            <Textarea
              name="solution"
              value={formData.solution}
              onChange={handleChange}
              required
              rows={3}
              placeholder="Describe the solution provided..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Results *
            </label>
            {formData.results.map((result, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <Input
                  value={result}
                  onChange={(e) => handleResultChange(index, e.target.value)}
                  placeholder={`Result ${index + 1}`}
                />
                {formData.results.length > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => removeResult(index)}
                  >
                    <X size={16} />
                  </Button>
                )}
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={addResult}
              className="mt-2"
            >
              Add Result
            </Button>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Image URL *
            </label>
            <Input
              name="image"
              value={formData.image}
              onChange={handleChange}
              required
              placeholder="https://images.unsplash.com/photo-..."
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_published"
              name="is_published"
              checked={formData.is_published}
              onChange={handleChange}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <label htmlFor="is_published" className="ml-2 text-sm text-slate-700">
              Publish immediately
            </label>
          </div>

          <div className="flex justify-end space-x-4 pt-6 border-t border-slate-200">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              {loading ? 'Saving...' : 'Save Case Study'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CaseStudyForm;
